import { useEffect, useCallback, useMemo } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  BackgroundVariant,
  Panel,
  NodeTypes,
  EdgeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import NodeRedNode, { NodeRedNodeData } from './NodeRedNode'
import CustomEdge from './CustomEdge'

// Node-RED node type
export interface NodeRedFlowNode {
  id: string
  type: string
  name?: string
  label?: string
  x?: number
  y?: number
  wires?: string[][]
  [key: string]: any
}

interface FlowCanvasProps {
  nodes: NodeRedFlowNode[]
  onNodesCountChange?: (count: number) => void
  onEdgesCountChange?: (count: number) => void
}

// Node type colors mapping (darker colors for better text visibility)
const getNodeColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    'inject': '#d97706',        // Darker orange
    'debug': '#16a34a',         // Darker green
    'function': '#ca8a04',      // Darker yellow
    'change': '#a16207',        // Dark amber
    'switch': '#d97706',        // Orange
    'template': '#0891b2',      // Darker cyan
    'mqtt in': '#64748b',       // Slate
    'mqtt out': '#64748b',      // Slate
    'http in': '#0284c7',       // Darker sky blue
    'http response': '#0284c7', // Darker sky blue
    'http request': '#0284c7',  // Darker sky blue
    'csv': '#b45309',           // Dark amber
    'split': '#7c3aed',         // Violet
    'join': '#9333ea',          // Purple
    'sort': '#4f46e5',          // Indigo
    'batch': '#6366f1',         // Light indigo
    'file': '#6b7280',          // Gray
    'file in': '#6b7280',       // Gray
    'delay': '#dc2626',         // Red
    'trigger': '#ea580c',       // Dark orange
    'exec': '#7c2d12',          // Dark brown
    'link in': '#0891b2',       // Cyan
    'link out': '#0891b2',      // Cyan
  }

  // If type is in the color map, return it
  if (colorMap[type]) {
    return colorMap[type]
  }

  // Generate color based on first character of type
  const firstChar = type.charAt(0).toLowerCase()
  const charCode = firstChar.charCodeAt(0)

  // Color palette for auto-generation (all darker colors for text visibility)
  const autoColors = [
    '#dc2626', // Red
    '#ea580c', // Dark orange
    '#d97706', // Amber
    '#ca8a04', // Yellow
    '#16a34a', // Green
    '#059669', // Emerald
    '#0891b2', // Cyan
    '#0284c7', // Sky blue
    '#2563eb', // Blue
    '#4f46e5', // Indigo
    '#7c3aed', // Violet
    '#9333ea', // Purple
    '#c026d3', // Fuchsia
    '#db2777', // Pink
    '#64748b', // Slate
    '#6b7280', // Gray
  ]

  // Use character code to select color (same first char = same color)
  const colorIndex = charCode % autoColors.length
  return autoColors[colorIndex]
}

// Convert Node-RED nodes to ReactFlow format
const convertToReactFlow = (nodeRedNodes: NodeRedFlowNode[]): { nodes: Node[]; edges: Edge[] } => {
  // Build node lookup map
  const nodeMap = new Map<string, NodeRedFlowNode>()
  nodeRedNodes.forEach(node => nodeMap.set(node.id, node))

  // Create ReactFlow nodes
  const flowNodes: Node[] = nodeRedNodes.map(node => {
    const outputCount = node.wires ? node.wires.length : 1

    return {
      id: node.id,
      type: 'nodeRedNode',
      position: { x: node.x || 0, y: node.y || 0 },
      data: {
        label: node.name || node.label || node.type,
        type: node.type,
        color: getNodeColor(node.type),
        outputCount,
      },
    }
  })

  // Create ReactFlow edges
  const flowEdges: Edge[] = []
  nodeRedNodes.forEach(node => {
    if (node.wires && Array.isArray(node.wires)) {
      node.wires.forEach((wireGroup, outputIndex) => {
        if (Array.isArray(wireGroup)) {
          wireGroup.forEach((targetId, wireIndex) => {
            // Only create edge if target node exists
            if (nodeMap.has(targetId)) {
              flowEdges.push({
                id: `e${node.id}-${outputIndex}-${wireIndex}-${targetId}`,
                source: node.id,
                target: targetId,
                sourceHandle: `output-${outputIndex}`,
                targetHandle: 'input',
                type: 'custom',
                animated: false,
              })
            }
          })
        }
      })
    }
  })

  return { nodes: flowNodes, edges: flowEdges }
}

// Define custom node types
const nodeTypes: NodeTypes = {
  nodeRedNode: NodeRedNode,
}

// Define custom edge types
const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
}

export default function FlowCanvas({ nodes: nodeRedNodes, onNodesCountChange, onEdgesCountChange }: FlowCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  // Convert and update nodes/edges when input changes
  useEffect(() => {
    const { nodes: flowNodes, edges: flowEdges } = convertToReactFlow(nodeRedNodes)
    setNodes(flowNodes)
    setEdges(flowEdges)

    // Notify parent of counts
    if (onNodesCountChange) onNodesCountChange(flowNodes.length)
    if (onEdgesCountChange) onEdgesCountChange(flowEdges.length)
  }, [nodeRedNodes, setNodes, setEdges, onNodesCountChange, onEdgesCountChange])

  // Handle edge connections
  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  )

  // MiniMap node color function
  const minimapNodeColor = useCallback((node: Node): string => {
    return (node.data.color as string) || '#999999'
  }, [])

  return (
    <div className="w-full h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          minZoom: 0.5,
          maxZoom: 1,
        }}
        attributionPosition="bottom-left"
        minZoom={0.1}
        maxZoom={4}
        defaultEdgeOptions={{
          type: 'custom',
          animated: false,
        }}
        // Performance optimizations
        elementsSelectable={true}
        nodesConnectable={true}
        nodesDraggable={true}
        zoomOnScroll={true}
        panOnScroll={false}
        panOnDrag={true}
        selectNodesOnDrag={false}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          color="#d0d0d0"
        />
        <Controls
          showZoom
          showFitView
          showInteractive
        />
        <MiniMap
          nodeColor={minimapNodeColor}
          maskColor="rgb(240, 240, 240, 0.6)"
          pannable
          zoomable
        />
        <Panel position="top-right" className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm text-xs text-gray-600 border border-gray-200">
          <div className="flex flex-col gap-1">
            <div><span className="font-semibold">{nodes.length}</span> nodes</div>
            <div><span className="font-semibold">{edges.length}</span> connections</div>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  )
}
